import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import andersen from '../../../assets/images/setupGuide/avatars/andersen.jpg';
import harper from '../../../assets/images/setupGuide/avatars/harper.jpg';
import jill from '../../../assets/images/setupGuide/avatars/jill.jpg';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  getHasIndividualTrainingAccess,
  getHasWebinarTrainingAccess,
} from '../../../store/data/company/companySelectors';
import {getWebinarTrainingUrl} from '../../../store/stateless/navigationSelectors';
import {useIndividualTrainingChiliPiperDialog} from '../chiliPiper/individualTrainingChiliPiperDialog';
import {SetupGuideHelpCardAvatar} from './setupGuideHelpCardAvatar';

/*
 * Constants.
 */

const avatars = [andersen, harper, jill];

/*
 * Props.
 */

interface SetupGuideHelpCardProps {
  onMoreResourcesClick: () => void;
}

/*
 * Style.
 */

const StyledRootDiv = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 8px;
  background-color: ${(p) => p.theme.greys.white};
  border-radius: 16px;
`;

const StyledContentDiv = styled.div`
  width: 176px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const StyledTitleContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledTitleAvatarsDiv = styled.div`
  width: 64px;
  height: 24px;
  display: flex;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  line-height: 128.571%;
`;

const StyledSubtitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  line-height: 150%;
`;

const StyledCtaContainerDiv = styled.div`
  display: flex;
`;

const StyledCtaButton = styled(Button)`
  color: ${(p) => p.theme.greys.shade80};
`;

/*
 * Component.
 */

export const SetupGuideHelpCard: FC<SetupGuideHelpCardProps> = (props) => {
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();

  const hasIndividualTrainingAccess = useAppSelector(getHasIndividualTrainingAccess);
  const hasWebinarTrainingAccess = useAppSelector(getHasWebinarTrainingAccess);
  const webinarTrainingUrl = useAppSelector(getWebinarTrainingUrl);

  const onMoreResourcesClick = () => {
    props.onMoreResourcesClick();
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ADMIN_SETUP_GUIDE_MORE_RESOURCES);
  };

  const [individualTrainingDialog, onIndividualTrainingClick] = useIndividualTrainingChiliPiperDialog(
    OnboardingDataEventObject.INDIVIDUAL_TRAINING,
  );

  const title = (
    <StyledTitleContainerDiv>
      <StyledTitleAvatarsDiv>
        {avatars.map((avatar, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <SetupGuideHelpCardAvatar key={i} imageSrc={avatar} index={i} />
        ))}
      </StyledTitleAvatarsDiv>
      <StyledTitleDiv>
        <Trans id="setup-guide-help-card-need-help">Need setup help?</Trans>
      </StyledTitleDiv>
    </StyledTitleContainerDiv>
  );

  const moreResourcesCta = (
    <HelpCardCta
      iconName="helpSmall"
      label={<Trans id="setup-guide-help-card-more-resources">More resources</Trans>}
      size={VisualSizesEnum.SMALL}
      onClick={onMoreResourcesClick}
    />
  );

  if (hasIndividualTrainingAccess) {
    return (
      <>
        <StyledRootDiv>
          <StyledContentDiv>
            {title}
            <StyledSubtitleDiv>
              <Trans id="setup-guide-help-card-get-call">
                Get your questions answered in a 1:1 call with our team.
              </Trans>
            </StyledSubtitleDiv>
          </StyledContentDiv>
          <HelpCardCta
            iconName="dateSmall"
            label={<Trans id="setup-guide-help-card-schedule-call">Schedule a call</Trans>}
            size={VisualSizesEnum.SMALL}
            onClick={onIndividualTrainingClick}
          />
          {moreResourcesCta}
        </StyledRootDiv>
        {individualTrainingDialog}
      </>
    );
  }

  if (hasWebinarTrainingAccess) {
    return (
      <StyledRootDiv>
        <StyledContentDiv>
          {title}
          <StyledSubtitleDiv>
            <Trans id="setup-guide-help-card-get-live-training">
              Join a live session led by our product experts and get questions answered.
            </Trans>
          </StyledSubtitleDiv>
        </StyledContentDiv>
        <HelpCardCta
          iconName="dateSmall"
          label={<Trans id="setup-guide-help-card-book-live-training">Book live training</Trans>}
          size={VisualSizesEnum.SMALL}
          onClick={() => {
            trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.WEBINAR_TRAINING);
            navigateToLink(webinarTrainingUrl);
          }}
        />
        {moreResourcesCta}
      </StyledRootDiv>
    );
  }

  return (
    <HelpCardCta
      iconName="helpSmall"
      label={<Trans id="setup-guide-help-card-the-resources">Help resources</Trans>}
      size={VisualSizesEnum.MEDIUM}
      onClick={onMoreResourcesClick}
    />
  );
};

const HelpCardCta: FC<{iconName: IconName; label: ReactNode; size: VisualSizesEnum; onClick: () => void}> = (
  props,
) => {
  const theme = useUserTheme();

  return (
    <StyledCtaContainerDiv>
      <StyledCtaButton
        leftIcon={props.iconName}
        leftIconColor={theme.greys.shade80}
        onClick={props.onClick}
        size={props.size}
      >
        {props.label}
      </StyledCtaButton>
    </StyledCtaContainerDiv>
  );
};
